import React, { useMemo } from 'react'
import { Box, Text, Flex, Stack } from '@chakra-ui/core'
import styled from '@emotion/styled'
import { FileBarChart, MessageSquare, MessageCircle, CheckSquare, Zap, Library } from 'lucide-react'
import { SearchableMenu } from 'components/AdminConsole/commonUI/SearchableMenu'
import {
  PermissionItem,
  PermissionsData,
  PERMISSION_LEVELS,
  DEFAULT_PERMISSIONS,
  getMaxAllowedLevel,
  MainPermissionLevel,
  PERMISSIONS_LEVELS,
  SERVICE_BOUNDS,
  ServiceKey,
  BASE_PERMISSION,
  normalizePermissionForUI,
} from './types'
import useLocaleText from 'components/useLocaleText'
import LocaleText from 'components/LocaleText'

const TableContainer = styled(Box)`
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  overflow: hidden;
  background: white;
`

const TableHeader = styled(Flex)`
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px;
  font-weight: 600;
  font-size: 14px;
  color: #2d3748;
`

const TableRow = styled(Flex)`
  border-bottom: 1px solid #f1f5f9;
  padding: 16px;
  align-items: center;
  transition: background-color 0.2s;

  &:hover {
    background: #f7fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`

const TableCell = styled(Box)`
  display: flex;
  align-items: center;
`

interface PermissionsTableProps {
  permissions?: PermissionsData
  onChange?: (permissions: PermissionsData) => void
  isDisabled?: boolean
  mainPermission: number
}

export const PermissionsTable: React.FC<PermissionsTableProps> = ({
  permissions = DEFAULT_PERMISSIONS,
  onChange,
  isDisabled = false,
  mainPermission,
}) => {
  // const [searchTerm, setSearchTerm] = useState('')

  const admin_select_placeholder = useLocaleText('admin_select_placeholder')
  // const t_search = useLocaleText('search')

  const permissionItems: PermissionItem[] = useMemo(
    () => [
      {
        key: 'reports',
        name: 'reports',
        icon: <FileBarChart size={18} strokeWidth={2.5} />,
        permission: normalizePermissionForUI(permissions.reports ?? PERMISSIONS_LEVELS.EDIT),
      },
      {
        key: 'posts',
        name: 'posts',
        icon: <MessageSquare size={18} strokeWidth={2.5} />,
        permission: normalizePermissionForUI(permissions.posts ?? PERMISSIONS_LEVELS.EDIT),
      },
      {
        key: 'comments',
        name: 'comments',
        icon: <MessageCircle size={18} strokeWidth={2.5} />,
        permission: normalizePermissionForUI(permissions.comments ?? PERMISSIONS_LEVELS.EDIT),
      },
      {
        key: 'tasks',
        name: 'tasks',
        icon: <CheckSquare size={18} strokeWidth={2.5} />,
        permission: normalizePermissionForUI(permissions.tasks ?? PERMISSIONS_LEVELS.EDIT),
      },
      {
        key: 'pops',
        name: 'pops',
        icon: <Zap size={18} strokeWidth={2.5} />,
        permission: normalizePermissionForUI(permissions.pops ?? PERMISSIONS_LEVELS.EDIT),
      },
      {
        key: 'drive',
        // 'library' is the client-facing name for the drive service (translation reused from the feature).
        name: 'library',
        icon: <Library size={18} strokeWidth={2.5} />,
        permission: normalizePermissionForUI(permissions.drive ?? PERMISSIONS_LEVELS.VIEW),
      },
    ],
    [permissions]
  )

  // const filteredItems = useMemo(() => {
  //   if (!searchTerm) return permissionItems

  //   return permissionItems.filter(
  //     (item) =>
  //       item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       item.key.toLowerCase().includes(searchTerm.toLowerCase())
  //   )
  // }, [permissionItems, searchTerm])

  const handlePermissionChange = (key: string, level: number) => {
    if (!onChange) return

    const bounds = SERVICE_BOUNDS[key as unknown as ServiceKey]
    const mainMax = mainPermission
      ? getMaxAllowedLevel(mainPermission as MainPermissionLevel)
      : PERMISSIONS_LEVELS.APP_ADMIN
    const effectiveMax = Math.min(bounds.max, mainMax)
    const effectiveMin = bounds.min
    const clampedLevel = Math.min(Math.max(level, effectiveMin), effectiveMax)

    const newPermissions = {
      ...permissions,
      [key]: clampedLevel,
    }

    onChange(newPermissions)
  }

  const getAvailableOptions = (key: string) => {
    const bounds = SERVICE_BOUNDS[key as unknown as ServiceKey]
    const mainMax = mainPermission
      ? getMaxAllowedLevel(mainPermission as MainPermissionLevel)
      : PERMISSIONS_LEVELS.APP_ADMIN
    const effectiveMax = Math.min(bounds.max, mainMax)
    const effectiveMin = bounds.min

    return PERMISSION_LEVELS.filter((level) => level.value >= effectiveMin && level.value <= effectiveMax)
  }

  const getPermissionColor = (level: number) => {
    switch (level) {
      case PERMISSIONS_LEVELS.UNAUTHORIZED:
      case BASE_PERMISSION:
        return 'red.500'
      case PERMISSIONS_LEVELS.VIEW:
        return 'orange.500'
      case PERMISSIONS_LEVELS.EDIT:
        return 'blue.500'
      case PERMISSIONS_LEVELS.APP_ADMIN:
        return 'green.500'
      default:
        return 'gray.500'
    }
  }

  return (
    <Stack spacing={4}>
      {/* <StyledFormControl>
        <InputWithIcon
          placeholder={t_search}
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          icon={<Search size={16} />}
        />
      </StyledFormControl> */}

      <TableContainer>
        <TableHeader>
          <TableCell flex="1">
            <Flex align="center">
              <Text>
                <LocaleText text="name" isLocalUse />
              </Text>
            </Flex>
          </TableCell>
          <TableCell flex="0 0 200px" justifyContent="center">
            <Text>
              <LocaleText text="permissions" isLocalUse />
            </Text>
          </TableCell>
        </TableHeader>

        {permissionItems.map((item) => (
          <TableRow key={item.key}>
            <TableCell flex="1">
              <Flex align="center" style={{ columnGap: '10px' }}>
                <Box color="gray.600">{item.icon}</Box>
                <Text fontSize="14px" fontWeight="500" color="gray.800">
                  <LocaleText text={item.name} isLocalUse />
                </Text>
              </Flex>
            </TableCell>
            <TableCell flex="0 0 200px" justifyContent="center">
              <Box width="200px">
                <SearchableMenu
                  isDisabled={isDisabled}
                  isShowSearch={false}
                  items={getAvailableOptions(item.key).map((level) => ({
                    id: level.value.toString(),
                    name: level.name,
                    isShowId: false,
                    bulletColor: getPermissionColor(level.value),
                  }))}
                  selectedId={(function computeSelected() {
                    const bounds = SERVICE_BOUNDS[item.key as unknown as ServiceKey]
                    const mainMax = mainPermission
                      ? getMaxAllowedLevel(mainPermission as MainPermissionLevel)
                      : PERMISSIONS_LEVELS.APP_ADMIN
                    const effectiveMax = Math.min(bounds.max, mainMax)
                    const effectiveMin = bounds.min
                    // item.permission is already normalized by normalizePermissionForUI in useMemo
                    const clamped = Math.min(Math.max(item.permission, effectiveMin), effectiveMax)
                    return clamped.toString()
                  })()}
                  placeholder={admin_select_placeholder}
                  onSelect={(selectedLevel) => {
                    handlePermissionChange(item.key, Number(selectedLevel.id) as number)
                  }}
                />
              </Box>
            </TableCell>
          </TableRow>
        ))}

        {/* {permissionItems.length === 0 && (
          <Box py={8} textAlign="center">
            <Text color="gray.500" fontSize="14px">
              No permissions found matching &quot;{searchTerm}&quot;
            </Text>
          </Box>
        )} */}
      </TableContainer>
    </Stack>
  )
}
